import api from './axiosBase';
import { getAuth } from 'firebase/auth';

export interface LeadFormField {
  id: string;
  type: 'email' | 'phone' | 'name' | 'text' | 'checkbox';
  label: string;
  placeholder: string;
  required: boolean;
}

export interface LeadFormTrigger {
  type: 'immediate' | 'delay' | 'scroll' | 'exit_intent' | 'ai';
  delayMs: number;
  scrollPct: number;
  showOnce: boolean;
  pages: 'all' | 'homepage' | 'product' | 'collection';
  customPath: string;
}

export interface LeadFormStyle {
  backgroundColor: string;
  textColor: string;
  buttonColor: string;
  buttonTextColor: string;
  borderRadius: number;
  position: 'center' | 'bottom_right' | 'bottom_left';
}

export interface LeadFormTeaser {
  enabled: boolean;
  title: string;
  position: 'bottom_left' | 'bottom_right';
}

export interface LeadForm {
  id: string;
  name: string;
  formType: 'popup' | 'slide_in' | 'banner';
  trigger: LeadFormTrigger;
  style: LeadFormStyle;
  fields: LeadFormField[];
  heading: string;
  subheading: string;
  buttonText: string;
  successText: string;
  enabled: boolean;
  status?: string;
  rewardType?: string;
  rewardValue?: string;
  tags?: string[];
  teaser?: LeadFormTeaser;
  views?: number;
  submissions?: number;
  aiSummary?: string;
  hasCreativeQuestion?: boolean;
  insight?: string;
  insightUpdatedAt?: string;
  createdAt: string;
  updatedAt: string;
}

/** Fetch all lead forms for a project */
export async function getLeadForms(projectId: string): Promise<LeadForm[]> {
  const auth = getAuth();
  const idToken = await auth.currentUser?.getIdToken();
  const response = await api.get(`/v2/project/${projectId}/forms`, {
    headers: { Authorization: `Bearer ${idToken}` }
  });
  return response.data?.forms ?? [];
}

/** Create a new lead form */
export async function createLeadForm(
  projectId: string,
  form: Omit<LeadForm, 'id' | 'createdAt' | 'updatedAt'>
): Promise<LeadForm> {
  const auth = getAuth();
  const idToken = await auth.currentUser?.getIdToken();
  const response = await api.post(`/v2/project/${projectId}/forms`, form, {
    headers: { Authorization: `Bearer ${idToken}` }
  });
  return response.data.form;
}

/** Update an existing lead form */
export async function updateLeadForm(
  projectId: string,
  formId: string,
  form: Partial<LeadForm>
): Promise<LeadForm> {
  const auth = getAuth();
  const idToken = await auth.currentUser?.getIdToken();
  const response = await api.put(
    `/v2/project/${projectId}/forms/${formId}`,
    form,
    { headers: { Authorization: `Bearer ${idToken}` } }
  );
  return response.data.form;
}

/** Delete a lead form */
export async function deleteLeadForm(
  projectId: string,
  formId: string
): Promise<void> {
  const auth = getAuth();
  const idToken = await auth.currentUser?.getIdToken();
  await api.delete(`/v2/project/${projectId}/forms/${formId}`, {
    headers: { Authorization: `Bearer ${idToken}` }
  });
}

/** AI-generate a new form from a text prompt (backend auto-saves in create mode) */
export async function aiGenerateForm(
  projectId: string,
  prompt: string
): Promise<LeadForm> {
  const auth = getAuth();
  const idToken = await auth.currentUser?.getIdToken();
  const response = await api.post(
    `/v2/project/${projectId}/forms/ai`,
    { prompt, mode: 'create' },
    { headers: { Authorization: `Bearer ${idToken}` } }
  );
  return response.data.form;
}

/** AI-edit an existing form from a text prompt */
export async function aiEditForm(
  projectId: string,
  prompt: string,
  currentForm: LeadForm
): Promise<Omit<LeadForm, 'id' | 'createdAt' | 'updatedAt'>> {
  const auth = getAuth();
  const idToken = await auth.currentUser?.getIdToken();
  const response = await api.post(
    `/v2/project/${projectId}/forms/ai`,
    { prompt, mode: 'edit', currentForm },
    { headers: { Authorization: `Bearer ${idToken}` } }
  );
  return response.data.form;
}

/** Batch-generate walkthrough forms (2 per type = 6 total) */
export interface WalkthroughFormPair {
  type: string;
  a: LeadForm | null;
  b: LeadForm | null;
}

export interface WalkthroughFormsResponse {
  success: boolean;
  forms: LeadForm[];
  pairs: WalkthroughFormPair[];
  errors?: string[];
}

export async function aiGenerateWalkthroughForms(
  projectId: string
): Promise<WalkthroughFormsResponse> {
  const auth = getAuth();
  const idToken = await auth.currentUser?.getIdToken();
  const response = await api.post(
    `/v2/project/${projectId}/forms/ai-walkthrough`,
    {},
    { headers: { Authorization: `Bearer ${idToken}` } }
  );
  return response.data;
}

export async function resetWalkthroughForms(
  projectId: string
): Promise<WalkthroughFormsResponse> {
  const auth = getAuth();
  const idToken = await auth.currentUser?.getIdToken();
  const response = await api.post(
    `/v2/project/${projectId}/forms/ai-walkthrough/reset`,
    {},
    { headers: { Authorization: `Bearer ${idToken}` } }
  );
  return response.data;
}

export interface FormSubmission {
  userId: string;
  userName: string;
  userEmail: string;
  userPhone: string;
  timestamp: string;
  fields: { question: string; answer: string; type?: string }[];
  submissionNumber: number;
}

/** Fetch all submissions for a specific form */
export async function getFormSubmissions(
  projectId: string,
  formId: string
): Promise<{
  submissions: FormSubmission[];
  total: number;
  insight?: string;
  insightUpdatedAt?: string;
  hasCreativeQuestion?: boolean;
}> {
  const auth = getAuth();
  const idToken = await auth.currentUser?.getIdToken();
  const response = await api.get(
    `/v2/project/${projectId}/forms/${formId}/submissions`,
    { headers: { Authorization: `Bearer ${idToken}` } }
  );
  return response.data;
}
